import { formatInr } from "@/lib/format/inr";
import { formatRelativeTime } from "@/lib/format/relative-time";
import { CategoryIcon } from "@/components/category-icon/category-icon";
import type { GroupDetail } from "@/lib/groups/group-detail";
import type { GroupMemberItem } from "@/lib/groups/group-member-item";
import type { GroupExpenseItem } from "@/lib/groups/group-expense-item";
import type { GroupInviteItem } from "@/lib/groups/group-invite-item";
import { formatAdminDate, formatAdminDateTime } from "@/lib/format/admin-time";

export interface GroupProfileViewProps {
  group: GroupDetail;
  now: Date;
  members?: GroupMemberItem[];
  recentExpenses?: GroupExpenseItem[];
  inviteLinks?: GroupInviteItem[];
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map(p => p[0]?.toUpperCase() ?? "")
      .join("") || "??"
  );
}

// Moments: admins' clock (IST), labelled.
function toDateTime(d: Date | string | null): string {
  return d ? formatAdminDateTime(d) : "—";
}

function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "info";
  children: React.ReactNode;
}) {
  const cls = {
    neutral: "text-fc-text-secondary border-fc-border bg-fc-overlay",
    good: "text-fc-brand border-fc-brand/40 bg-fc-brand/10",
    warn: "text-fc-warn border-fc-warn/40 bg-fc-warn/10",
    info: "text-fc-info border-fc-info/40 bg-fc-info/10",
  }[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {children}
    </span>
  );
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-4">
      <div className="text-[10.5px] uppercase tracking-wide text-fc-text-dim">
        {label}
      </div>
      <div className="font-mono text-[22px] font-semibold tracking-tight text-fc-text">
        {value}
      </div>
      {sub ? (
        <div className="text-[10.5px] text-fc-text-dim">{sub}</div>
      ) : null}
    </div>
  );
}

function PropRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
        {label}
      </div>
      <div className="text-[12.5px] text-fc-text">{children}</div>
    </>
  );
}

export function GroupProfileView({
  group,
  now,
  members,
  recentExpenses,
  inviteLinks,
}: GroupProfileViewProps) {
  const memberCount = group.memberCount;
  const budgetSet = group.monthlyBudget != null;
  const showMembers = members && members.length > 0;
  const showExpenses = recentExpenses && recentExpenses.length > 0;
  const showInvites = inviteLinks && inviteLinks.length > 0;
  const budgetUsedPct =
    budgetSet && group.monthlyBudget! > 0
      ? Math.round((group.monthSpent / group.monthlyBudget!) * 100)
      : null;

  const emoji =
    group.emoji ?? (group.isPersonal ? "👤" : "👥");

  return (
    <div className="flex flex-col gap-6">
      {/* HERO */}
      <section className="flex items-center gap-4">
        <div className="grid h-[72px] w-[72px] place-items-center rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-700 text-[32px]">
          {emoji}
        </div>
        <div className="min-w-0">
          <h1 className="text-[24px] font-semibold tracking-tight text-fc-text">
            {group.name}
          </h1>
          <div className="text-[12.5px] text-fc-text-dim">
            created by{" "}
            <span className="text-fc-text-secondary">
              {group.createdByName}
            </span>{" "}
            · {formatAdminDate(group.createdAt)}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip tone="info">{group.isPersonal ? "personal" : "family"}</Chip>
            <Chip>
              {memberCount} member{memberCount === 1 ? "" : "s"}
            </Chip>
            {budgetSet ? <Chip tone="good">budget set</Chip> : null}
            {group.inviteLinkCount > 0 ? (
              <Chip tone="warn">
                {group.inviteLinkCount} active invite
                {group.inviteLinkCount === 1 ? "" : "s"}
              </Chip>
            ) : null}
            <span className="font-mono text-[10.5px] tracking-wider text-fc-text-dim">
              g_{group.id.slice(0, 8)}
            </span>
          </div>
        </div>
      </section>

      {/* METRIC STRIP */}
      <section className="grid grid-cols-4 gap-3">
        <Metric
          label="Members"
          value={String(group.memberCount)}
          sub={`${group.adminCount} admin · ${
            group.memberCount - group.adminCount
          } member`}
        />
        <Metric label="Expenses" value={String(group.expenseCount)} />
        <Metric label="Total spend" value={formatInr(group.totalSpent)} />
        <Metric
          label="Monthly budget"
          value={group.monthlyBudget != null ? formatInr(group.monthlyBudget) : "—"}
          sub={budgetUsedPct != null ? `used ${budgetUsedPct}%` : undefined}
        />
      </section>

      {/* MEMBERS */}
      {showMembers ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[13px] font-semibold text-fc-text">
                Members
              </div>
              <div className="text-[11.5px] text-fc-text-dim">
                Members list · {members.length} total
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {members.map(m => (
              <div
                key={m.userId}
                className="grid grid-cols-[32px_1fr_auto_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
              >
                <div className="grid h-8 w-8 place-items-center rounded-full border border-fc-border bg-fc-overlay text-[11.5px] font-semibold text-fc-text-secondary">
                  {initials(m.name)}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-medium text-fc-text">
                    {m.name}
                  </div>
                  <div className="truncate text-[11px] text-fc-text-dim">
                    {m.email}
                  </div>
                </div>
                <Chip tone={m.role === "admin" ? "good" : "neutral"}>
                  {m.role}
                </Chip>
                <span className="font-mono text-[11px] text-fc-text-dim">
                  joined {formatRelativeTime(m.joinedAt, now)}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* MEMO */}
      {group.memo ? (
        <section className="rounded-xl border border-fc-info/20 bg-fc-info/5 p-4">
          <div className="text-[10.5px] uppercase tracking-wide text-fc-info">
            Family memo
          </div>
          <div className="mt-1.5 text-[13px] text-fc-text">
            &ldquo;{group.memo}&rdquo;
          </div>
          {group.memoUpdatedByName ? (
            <div className="mt-1 text-[11px] text-fc-text-dim">
              by {group.memoUpdatedByName}
              {group.memoUpdatedAt
                ? ` · ${formatRelativeTime(group.memoUpdatedAt, now)}`
                : ""}
            </div>
          ) : null}
        </section>
      ) : null}

      {/* RECENT EXPENSES */}
      {showExpenses ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[13px] font-semibold text-fc-text">
                Recent expenses
              </div>
              <div className="text-[11.5px] text-fc-text-dim">
                Last {recentExpenses.length}
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {recentExpenses.map(e => (
              <div
                key={e.id}
                className="grid grid-cols-[32px_1fr_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
              >
                <CategoryIcon emoji={e.categoryEmoji} size="sm" />
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] text-fc-text">
                    <span className="font-semibold">{e.categoryName}</span>
                    <span className="text-fc-text-dim"> · paid by </span>
                    <span>{e.payerName}</span>
                    {e.note ? (
                      <>
                        <span className="text-fc-text-dim"> · </span>
                        <span className="text-fc-text-secondary">{e.note}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="font-mono text-[12.5px] font-semibold text-fc-text">
                    {formatInr(e.amount)}
                  </span>
                  <span className="font-mono text-[11px] text-fc-text-dim">
                    {formatRelativeTime(e.createdAt, now)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* INVITE LINKS */}
      {showInvites ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[13px] font-semibold text-fc-text">
                Invite links
              </div>
              <div className="text-[11.5px] text-fc-text-dim">
                {inviteLinks.length} total
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {inviteLinks.map(i => {
              // Links don't expire; only revoking turns one off.
              const state = i.isRevoked
                ? { tone: "warn" as const, label: "revoked" }
                : { tone: "good" as const, label: "active" };
              return (
                <div
                  key={i.id}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-[11.5px] text-fc-text-secondary">
                      {i.tokenPrefix}…
                    </div>
                    <div className="text-[11px] text-fc-text-dim">
                      by {i.createdByName}
                    </div>
                  </div>
                  <div className="text-[11px] text-fc-text-dim">
                    created{" "}
                    <span className="font-mono">
                      {formatRelativeTime(i.createdAt, now)}
                    </span>
                  </div>
                  <Chip tone={state.tone}>{state.label}</Chip>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* PROP GRID */}
      <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
        <div className="mb-4">
          <div className="text-[13px] font-semibold text-fc-text">
            Group properties
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            Fields from schema
          </div>
        </div>
        <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5">
          <PropRow label="Group ID">
            <span className="rounded-md border border-fc-border bg-fc-overlay px-2 py-0.5 font-mono text-[11.5px]">
              {group.id}
            </span>
          </PropRow>
          <PropRow label="Type">
            {group.isPersonal ? "Personal (isPersonal = true)" : "Family (isPersonal = false)"}
          </PropRow>
          <PropRow label="Created by">{group.createdByName}</PropRow>
          <PropRow label="Monthly budget">
            {group.monthlyBudget != null ? (
              <>
                <span className="font-mono font-semibold">
                  {formatInr(group.monthlyBudget)}
                </span>
                {budgetUsedPct != null ? (
                  <>
                    {" "}· used{" "}
                    <span className="font-mono">{formatInr(group.monthSpent)}</span>{" "}
                    ({budgetUsedPct}%)
                  </>
                ) : null}
              </>
            ) : (
              "—"
            )}
          </PropRow>
          <PropRow label="Created">
            <span className="font-mono text-[12px]">
              {toDateTime(group.createdAt)}
            </span>
          </PropRow>
          <PropRow label="Updated">
            <span className="font-mono text-[12px]">
              {toDateTime(group.updatedAt)}
            </span>
          </PropRow>
          <PropRow label="Active invites">
            {group.inviteLinkCount > 0 ? (
              <Chip tone="warn">
                {group.inviteLinkCount} active
              </Chip>
            ) : (
              "—"
            )}
          </PropRow>
          <PropRow label="Recurring expenses">{group.recurringExpenseCount}</PropRow>
        </div>
      </section>
    </div>
  );
}
